import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { createConsultation } from "../lib/api";
import { SERVICES } from "../lib/data";
import { Reveal } from "../components/Reveal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { CalendarIcon, Loader2, CheckCircle2, Phone, Mail, MapPin, Instagram } from "lucide-react";
import { toast } from "sonner";

const CONSULT_BG =
  "https://images.pexels.com/photos/33529508/pexels-photo-33529508.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ALL_SERVICES = [
  ...SERVICES.map((s) => s.name),
  "CO2 Fractional Laser",
  "IPL Photofacial",
  "PRP Therapy",
  "IV Hydration Therapy",
  "Weight Management (GLP-1)",
  "Mesotherapy",
  "HIFU Skin Lifting",
  "Hydra-Beauty Facial",
  "Wet Cupping",
  "Dry Cupping",
  "Moving Cupping",
  "Free Consultation",
  "Not sure — recommend for me",
];
// Dedupe while preserving order
const UNIQUE_SERVICES = [...new Set(ALL_SERVICES)];

export default function Consultation() {
  const [params] = useSearchParams();
  const preselect = params.get("service") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselect) {
      // If the preselect exactly matches a service, use it; otherwise use "Not sure"
      const match = UNIQUE_SERVICES.find((s) => s.toLowerCase() === preselect.toLowerCase());
      setForm((f) => ({ ...f, service: match || preselect }));
    }
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await createConsultation({
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : "",
        preferred_time: time || "",
      });
      setDone(true);
      toast.success("Consultation request sent!");
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <main className="min-h-screen flex items-center justify-center relative z-10 px-6">
        <div data-testid="consultation-success" className="text-center max-w-lg">
          <CheckCircle2 className="w-16 h-16 text-[#4A3D6B] mx-auto mb-6" strokeWidth={1.2} />
          <h1 className="font-display text-5xl tracking-tight mb-4">We&apos;ll be in touch</h1>
          <p className="text-[#57515F]">Thank you, {form.name.split(" ")[0]}. Our team will reach out shortly to schedule your complimentary consultation.</p>
        </div>
      </main>
    );

  return (
    <main className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <div>
            <p className="overline text-[#B0872A] mb-4">Complimentary</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6">
              Free consultation
            </h1>
            <p className="text-[#57515F] text-lg max-w-md mb-10">
              Meet with our specialists to map your skin and design a personalised treatment
              journey. No obligation — only expert guidance.
            </p>
            <div className="rounded-2xl overflow-hidden h-72 hover-scale-img">
              <img src={CONSULT_BG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="mt-8 space-y-3" data-testid="consult-contact">
              <p className="overline text-[#B0872A]">Get in touch</p>
              <a href="tel:+16616668041" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#B0872A] transition-colors"><Phone className="w-4 h-4 text-[#B0872A]" strokeWidth={1.5} /> +1 (661) 666-8041</a>
              <a href="mailto:Womenheaven@outlook.com" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#B0872A] transition-colors"><Mail className="w-4 h-4 text-[#B0872A]" strokeWidth={1.5} /> Womenheaven@outlook.com</a>
              <p className="flex items-center gap-3 text-[#57515F]"><MapPin className="w-4 h-4 text-[#B0872A]" strokeWidth={1.5} /> Santa Clarita, California</p>
              <a href="https://instagram.com/womenheavenmedspa" target="_blank" rel="noreferrer" data-testid="consult-instagram" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#B0872A] transition-colors"><Instagram className="w-4 h-4 text-[#B0872A]" strokeWidth={1.5} /> @womenheavenmedspa</a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="consultation-form" onSubmit={submit} className="space-y-8">
            <div>
              <label className="overline block mb-2">Full Name</label>
              <input data-testid="consult-name" className="input-line" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="overline block mb-2">Email</label>
                <input data-testid="consult-email" type="email" className="input-line" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label className="overline block mb-2">Phone</label>
                <input data-testid="consult-phone" className="input-line" value={form.phone} onChange={set("phone")} placeholder="+1 305 …" />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Service of Interest</label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="consult-service" className="input-line h-auto data-[placeholder]:text-[#8B8494]">
                  <SelectValue placeholder="Select a treatment" />
                </SelectTrigger>
                <SelectContent>
                  {UNIQUE_SERVICES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Preferred Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="consult-date" className="input-line flex items-center justify-between text-left">
                      {date ? format(date, "PPP") : <span className="text-[#8B8494]">Pick a date</span>}
                      <CalendarIcon className="w-4 h-4 text-[#8B8494]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="overline block mb-2">Preferred Time</label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="consult-time">
                    <SelectValue placeholder="Pick a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 18 }).map((_, i) => {
                      const h = 9 + Math.floor(i / 2);
                      const m = i % 2 === 0 ? "00" : "30";
                      const val = `${String(h).padStart(2, "0")}:${m}`;
                      const label = `${((h + 11) % 12) + 1}:${m} ${h < 12 ? "AM" : "PM"}`;
                      return <SelectItem key={val} value={val}>{label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Message (optional)</label>
              <Textarea data-testid="consult-message" value={form.message} onChange={set("message")} placeholder="Tell us about your goals…" className="bg-transparent border border-[#DAD5E2] rounded-lg" />
            </div>

            <button data-testid="consult-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Consultation"}
            </button>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
